import { Menu } from './menu';
import { Security } from '../containers/Authentication/reducer';

namespace Api {

	export enum DriveItemType {
		Directory = 0,
		Component = 1,
		File = 2,
		Template = 3,
		Image = 4,
		Movie = 5,
		Publication = 6,
		Sound = 7,
		V2Gateway = 8,
		Link = 9,
		AppContainer = 10,
		Gallery = 11,
		ExternalLink = 12,
	}

	export interface NestedAppMeta {
		app: NestedDriveItems;
		sharedDir: NestedDriveItems;
	}

	export interface DriveItemResource {
		id: number;
		block?: number;
		type: DriveItemType;
		position?: number;
	}

	export enum AspFolderDisplayType {
		List = 'LIST',
		Grid = 'GRID',
		Masonry = 'MASONRY',
		Coverflow = 'COVERFLOW',
	}

	// TODO: separate typing by type, example, it seems only ExternalLink can have an url attribute
	export interface DriveItem {
		id: number;
		resolution?: string;
		label: string;
		name: string;
		originalFile: string;
		type: DriveItemType;
		src: string;
		version: number;
		icon?: string;
		titles: { [k: string]: string };
		associatedResolutions?: { [k: number]: string }; // sometimes not present in the api response
		parent: number;
		descriptions: { [k: string]: string };
		originalFileSize?: number;
		creationDate: string;
		lastUpdateDate: string;
		endDate?: string;
		pages?: number;
		url?: string;
		thumbsPath?: string;
		format?: string;
		privateDirId?: number;
		sharedDirId?: number;
		displayMode?: AspFolderDisplayType;
		status?: AssetItemStatus;
		componentType?: ComponentType;
		fileType?: AssetItemFileFormats;
		proposalStats?: ProposalData;
		linkId?: number;
		position?: number;
	}

	export interface NestedDriveItems extends DriveItem {
		resources?: NestedDriveItems[];
		resolutions?: { [k: string]: ResolutionGroup };
	}
	// same as driveItem but delivered by meta.json API
	export interface DriveFolderInfo extends DriveItem {
		blocksNumber?: number;
		resolutions?: { [k: string]: ResolutionGroup };
		resources?: DriveItemResource[];
		resolutionsBlock?: number;
	}

	export interface ResolutionSize {
		box: boolean;
		extension: string;
		width: number;
		height: number;
		ignoreUpscale: boolean;
	}

	export interface ResolutionGroup {
		sizes: ResolutionSize[];
		supported: string[];
	}

	export interface DriveItemContentBlock {
		metas: { [k: number]: DriveItem };
		resolutions?: { [k: string]: ResolutionGroup };
	}

	export enum OutcomingType {
		Directory = 'DIRECTORY',
		Component = 'COMPONENT',
		File = 'FILE',
		Template = 'TEMPLATE',
		Image = 'IMAGE',
		Movie = 'MOVIE',
		Publication = 'PUBLICATION',
		Sound = 'SOUND',
		V2Gateway = 'V2GATEWAY',
		Link = 'LINK',
		AppContainer = 'APP_CONTAINER',
		Gallery = 'GALLERY',
		ExternalLink = 'EXTERNAL_LINK'
	}

	export interface Outcoming {
		external?: boolean;
		id: number;
		type: OutcomingType;
		version: string;
		outcomings?: Outcoming[];
		resource?: Outcoming;
	}

	export interface Config {
		applicationGallery: number;
		applicationUrl: string;
		clientId: number;
		locale: string;
		type: number;
		mediaPath?: string;
		service?: Service;
		downloadAllPopup?: DownloadAllPopupConfig;
	}

	export interface Template {
		commonStyles: CommonStyles;
		sharedMethods?: ShareMethod[];
		brand?: BrandInfos;
		welcomepage?: WelcomePage;
		locale: string;
		menu?: Menu;
		applicationSplash?: ApplicationSplash;
	}

	export interface ApplicationSplash {
		background?: string;
		image?: string;
		spinnerWheelColor?: string;
	}

	export interface WelcomePage {
		url: string;
	}

	export interface BrandInfos {
		url: string;
		name: string;
		logo: string;
		logoHome: string;
	}

	export interface DownloadAllPopupConfig {
		enabled: boolean;
	}

	export interface CommonStyles {
		primary: PrimaryStyle;
	}

	export interface PrimaryStyle {
		actionColor: string;
		backgroundColor: string;
		cardColor: string;
		contentColor: string;
		spacerColor: string;
		strongColor: string;
		topnavColor: string;
		iconsColor: string;
		topnavOpacity: number;
		thumbBorderColor: string;
	}

	export interface SecondaryStyle {
		activeColor: string;
		alertColor: string;
		altContentColor: string;
		altPopupColor: string;
		notificationColor: string;
	}

	export interface TouchStyle {
		primaryBackgroundColor: string;
		primaryCardColor: string;
		primaryContentColor: string;
		pushmenuBackgroundColor: string;
		secondaryAlertColor: string;
	}

	export interface PushMenuStyle {
		activeColor: string;
		backgroundColor: string;
		contentColor: string;
		iconColor: string;
		spacerColor: string;
		strongBackgroundColor: string;
		strongColor: string;
	}

	export interface Service {
		host: string;
		directHost?: string;
		token: string;
		email: EmailService;
		download: DownloadService;
	}

	export interface EmailService {
		service: string;
	}

	export interface DownloadService {
		service: string;
	}

	export enum ShareMethod {
		Download = 'download',
		Share = 'share'
	}

	enum AssetItemAccessType {
		PUBLIC = 'PUBLIC',
	}

	enum AssetItemInvoiceStatus {
		CHARMED = 'CHARMED',
	}

	export enum AssetItemStatus {
		LIVE = 'LIVE',
		PROCESSING = 'PROCESSING',
		ERROR = 'ERROR',
	}

	export enum AssetItemFileTypes {
		DIRECTORY = 'DIRECTORY',
		COMPONENT = 'COMPONENT',
		GALLERY = 'GALLERY',
		IMAGE = 'IMAGE',
		PUBLICATION = 'PUBLICATION',
		TEMPLATE = 'TEMPLATE',
		MOVIE = 'MOVIE',
		FILE = 'FILE',
		V2GATEWAY = 'V2GATEWAY',
		EXTERNAL_LINK = 'EXTERNAL_LINK',
		APP_CONTAINER = 'APP_CONTAINER',
		LINK = 'LINK',
		PROPOSAL = 'PROPOSAL',
		DEFAULT = 'DEFAULT',
	}

	export enum AssetItemFileFormats {
		DOCUMENT = 'DOCUMENT',
		PRESENTATION = 'PRESENTATION',
		SPREADSHEET = 'SPREADSHEET',
		INDESIGN = 'INDESIGN',
		ILLUSTRATOR = 'ILLUSTRATOR',
		PHOTOSHOP = 'PHOTOSHOP',
		FILE = 'FILE',
		PDF = 'PDF',
		JPG = 'JPG',
		JPEG = 'JPEG',
		PNG = 'PNG',
		SVG = 'SVG',
		GIF = 'GIF',
		MOV = 'MOV',
		MP4 = 'MP4',
		AVI = 'AVI',
	}

	export interface AspDriveResourceItem {
		accessType: AssetItemAccessType;
		beginDate: {
			date: number;
			month: number;
			year: number;
		};
		clientId: number;
		clonable: boolean;
		endDate: number | null;
		expired: boolean;
		filesize: number;
		folder: boolean;
		forbidUpdateDate: number | null;
		forbiddenUpdate: boolean;
		genericItemCreationDate: number;
		genericItemId: number;
		genericItemLastUpdate: number;
		genericItemName: string;
		globalId: number;
		iconName: string | null;
		invoiceStatus: AssetItemInvoiceStatus;
		label: string;
		modelId: number;
		modelName: string;
		parentDriveGId: number | null;
		position: number;
		pushable: boolean;
		referent: AspDriveResourceItem | null;
		relUrl: string;
		secured: boolean;
		size: number;
		status: AssetItemStatus;
		type: AssetItemFileTypes;
		fileType: AssetItemFileFormats;
		format: PublicationFormat;
		version: number;
		sendingDate: number;
		openingDate: number;
		readsNbr: number;
		recipientEmail: string;
	}

	export interface AppLoginResponse {
		login: string;
		clientId: number;
		associatedApps: AspDriveResourceItem[];
		token: string;
		oneSessPerUser: boolean;
	}

	export interface AuthMethodsResponse {
		orgAuthMethods: Security[];
	}

	export enum CustomerContextRoles {
		ROLE_PRODUCTION = 'ROLE_PRODUCTION',
		ROLE_COMMERCIAL_SALESFORCE = 'ROLE_COMMERCIAL_SALESFORCE',
		ROLE_COMMERCIAL_MANUAL = 'ROLE_COMMERCIAL_MANUAL',
		ROLE_AGENCY = 'ROLE_AGENCY',
		ROLE_SAAS_ACCOUNT_MANAGER = 'ROLE_SAAS_ACCOUNT_MANAGER',
		ROLE_SAAS_USER = 'ROLE_SAAS_USER',
		ROLE_CONSUMER = 'ROLE_CONSUMER',
		ROLE_APPLICATION_USER = 'ROLE_APPLICATION_USER',
	}

	export interface CustomerAccount {
		autoRenew: boolean;
		availableCredits: number;
		company: string;
		creationDate: number;
		email: string;
		id: number;
		label: string;
		licenceId: number;
		locale: string;
		parentId: number;
		phone: string;
		role: CustomerContextRoles;
		saleName: string;
		status: 'ACTIVE' | 'DISABLED';
		type: string;
	}

	export interface GetClosestServerResponse {
		servers: { [k: string]: ServerInfos };
	}

	export interface ServerInfos {
		domain: string;
		closestServer: boolean;
	}

	export interface SharedContactInfo {
		company?: string;
		forename?: string;
		email: string;
		name?: string;
		phone?: string;
		position?: string;
	}

	interface SharedResource {
		id: number;
		url: string;
	}

	export interface SharedData {
		fromName: string;
		fromEmail: string;
		toAddresses: string[];
		message: string;
		language: string;
		resources: SharedResource[];
		contactInfo?: SharedContactInfo | {};
		subject?: string;
		createDeal?: boolean;
	}

	export interface CreateFolderData {
		driveLabel: string;
		clientId: number;
		parentGId: number;
		resourcesGIds: number[];
		displayMode?: string;
	}

	export interface IndesignEditorGeneralData {
		format: PublicationFormat;
		label: string;
		totalPages: number;
		pagePath: string;
		imagePath: string;
		thumbnailPath: string;
		cssPath?: string;
		configsPath?: string;
		jsPath?: string;
		nonEpubPages?: number[];
	}

	export interface EditorThumbnailData {
		label: string;
		position: number;
		type: AssetItemFileTypes;
		fileType: AssetItemFileFormats;
		proposal: boolean;
	}

	export interface IndesignEditorData {
		enrichmentInfo: IndesignEditorGeneralData;
		pagesData: EditorThumbnailData[];
	}

	export interface HtmlEditorData {
		label: string;
		pagePath: string;
	}

	export interface ImportFileData {
		clientId: number;
		resourceGId: number;
		resourceId: number;
		hardName: string;
	}

	export interface PublicationProgressData {
		percentage: number;
		status: AssetItemStatus;
	}

	export enum ComponentType {
		EPUB = 'EPUB',
		PDF = 'PDF',
		ZIP = 'ZIP',
		ZIP_EDITABLE = 'ZIP_EDITABLE',
	}

	export enum PublicationFormat {
		PDF = 'PDF',
		PDF_EDITABLE = 'PDF_EDITABLE',
		EPUB = 'EPUB',
		EPUB_MASTER = 'EPUB_MASTER',
		EPUB_EDITABLE = 'EPUB_EDITABLE',
		EPUB_PRESENTATION = 'EPUB_PRESENTATION',
	}

	export enum ProposalStatus {
		DRAFT = 'DRAFT',
		SENT = 'SENT',
		SEEN = 'SEEN',
	}

	export interface ProposalData {
		status: ProposalStatus;
		forbiddenUpdate: boolean;
		sentOn?: string;
		openedOn?: string;
		readsNbr?: number;
	}

	export interface ProposalTemplatesData {
		masterProposals: DriveItem[];
		customTemplates: DriveItem[];
		resolutions: { [k: string]: ResolutionGroup };
	}

	export interface ShareProposalData {
		pin: string;
		resourceUrl: string;
	}

	export enum CreationSource {
		DESKTOP = 'DESKTOP',
		PLUGIN = 'PLUGIN',
	}

	export interface RenameResourceData {
		newLabel: string;
	}

	export interface TrashResourcesData {
		undeletableResources: {
			[k: number]: string; // key is the id of the resource and the value is its label
		};
	}

	export interface ProfileData {
		id: number;
		clientId: number;
		name: string;
		email: string;
		position: string;
		phone: string | null;
		password: null;
		confirmPassword: null;
		photoPath: string | null;
		photoConfig: string | null;
		crmProviders: CRMProviderData[];
		emailProvider: EmailProviderData;
		role: CustomerContextRoles;
		oneSessPerUser: boolean;
	}

	export interface EditProfileData {
		profileInfo: string;
		profilePic?: File;
		removeProfilePic?: string;
	}

	export interface ProfilePhotoConfig {
		name: string;
		x: number;
		y: number;
		width: number;
		height: number;
	}

	export enum CRMProviders {
		NONE = 'NONE',
		HUBSPOT = 'HUBSPOT',
		SALESFORCE = 'SALESFORCE',
	}

	export interface CRMProviderData {
		provider: CRMProviders;
		integrationUrl: string;
		enabled: boolean;
		clientId?: string;
		clientSecret?: string;
	}

	enum EmailProviders {
		NYLAS = 'NYLAS',
	}

	export interface EmailProviderData {
		provider: EmailProviders;
		integrationUrl: string;
		enabled: boolean;
		email?: string;
	}

	export interface CrmContact {
		email: string | null;
		firstName: string | null;
		lastName: string | null;
		company: string | null;
	}

	// When the client is not connected to a CRM,
	// we get a status 304 with no body as a response
	export type getCrmContactsData = Api.CrmContact[] | '';

	export interface ProposalsByTypeData {
		list: AspDriveResourceItem[];
		currentPage: number;
		totalPage: number;
	}
}

export { Api };


// WEBPACK FOOTER //
// ./node_modules/tslint-loader!./src/typings/api.ts